// src/components/Sidebar.js
import React, { useContext } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Nav, Collapse } from "reactstrap";
import PerfectScrollbar from "perfect-scrollbar";
import { useAuth } from "../../context/AuthContext";
import { DataContext } from "../../context/DataContext";

// Fallback avatar in case user image URL is missing or broken
import fallbackAvatar from "assets/img/faces/placeholder.jpg";
import Logo from "assets/logo";

var ps;

function Sidebar(props) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // pull in user profile from DataContext
  const { data } = useContext(DataContext);
  const userProfile = data.userProfile || {};

  // Build an array of “effective roles”:
  //  - Always include the “role” field
  //  - If is_superuser is true, also include "superuser"
  const effectiveRoles = [];
  if (userProfile.role) {
    effectiveRoles.push(userProfile.role);
  }
  if (userProfile.is_superuser) {
    effectiveRoles.push("superuser");
  }

  const userName = userProfile.name || " ";
  const userImage = userProfile.image;

  const [openAvatar, setOpenAvatar] = React.useState(false);
  const [collapseStates, setCollapseStates] = React.useState({});

  const sidebar = React.useRef();

  // build collapse state from routes
  const getCollapseStates = (routes) => {
    let initialState = {};
    routes.forEach((prop) => {
      if (prop.collapse) {
        initialState[prop.state] = getCollapseInitialState(prop.views);
        Object.assign(initialState, getCollapseStates(prop.views));
      }
    });
    return initialState;
  };

  const getCollapseInitialState = (routes) => {
    return routes.some((r) =>
      (r.collapse && getCollapseInitialState(r.views)) ||
      location.pathname.includes(r.layout + r.path)
    );
  };

  // create sidebar links
  const createLinks = (routes) =>
    routes
      .filter((prop) => prop.show !== false)
      .map((prop, key) => {
        // If this route defines allowedRoles, but none of the user's effectiveRoles are included, skip it
        if (
          prop.allowedRoles &&
          !prop.allowedRoles.some((role) => effectiveRoles.includes(role))
        ) {
          return null;
        }

        if (prop.redirect) return null;

        if (prop.collapse) {
          return (
            <li
              className={getCollapseInitialState(prop.views) ? "active" : ""}
              key={key}
            >
              <a
                href="#pablo"
                data-toggle="collapse"
                aria-expanded={collapseStates[prop.state]}
                onClick={(e) => {
                  e.preventDefault();
                  setCollapseStates((prev) => ({
                    ...prev,
                    [prop.state]: !prev[prop.state],
                  }));
                }}
              >
                {prop.icon ? (
                  <>
                    <i className={prop.icon} />
                    <p>
                      {prop.name}
                      <b className="caret" />
                    </p>
                  </>
                ) : (
                  <>
                    <span className="sidebar-mini-icon">{prop.mini}</span>
                    <span className="sidebar-normal">
                      {prop.name}
                      <b className="caret" />
                    </span>
                  </>
                )}
              </a>
              <Collapse isOpen={collapseStates[prop.state]}>
                <ul className="nav">{createLinks(prop.views)}</ul>
              </Collapse>
            </li>
          );
        }

        return (
          <li
            className={
              location.pathname === prop.layout + prop.path ? "active" : ""
            }
            key={key}
          >
            <Link to={prop.layout + prop.path}>
              {prop.icon ? (
                <>
                  <i className={prop.icon} />
                  <p>{prop.name}</p>
                </>
              ) : (
                <>
                  <span className="sidebar-mini-icon">{prop.mini}</span>
                  <span className="sidebar-normal">{prop.name}</span>
                </>
              )}
            </Link>
          </li>
        );
      });

  // init scrollbar
  React.useEffect(() => {
    if (navigator.platform.includes("Win")) {
      ps = new PerfectScrollbar(sidebar.current, {
        suppressScrollX: true,
        suppressScrollY: false,
      });
    }
    return () => {
      if (navigator.platform.includes("Win") && ps) {
        ps.destroy();
      }
    };
  }, []);

  // build initial collapse states
  React.useEffect(() => {
    setCollapseStates(getCollapseStates(props.routes));
  }, [props.routes]);

  // perform logout and redirect
  const handleLogout = async () => {
    await logout();
    navigate("/auth/login", { replace: true });
  };

  return (
    <div className="sidebar" data-color={props.bgColor} data-active-color="danger">
      <div className="logo">
        <a href="#" className="d-flex align-items-center">
          <Logo width={65} height={65} color="white" />
          <div className="ml-2 text-white text-left">
            <span className="d-block font-weight-bold" style={{ fontSize: 14 }}>
              BEVERAGE
            </span>
            <span className="d-block font-weight-bold" style={{ fontSize: 14 }}>
              BROTHERS
            </span>
          </div>
        </a>
      </div>

      <div className="sidebar-wrapper" ref={sidebar}>
        <div className="user">
          <div className="photo" style={{ width: 35, height: 35, overflow: 'hidden', borderRadius: '50%' }}>
            <img
              src={userImage || fallbackAvatar}
              alt="Avatar"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = fallbackAvatar;
              }}
            />
          </div>
          <div className="info">
            <Link to="/admin/user-profile">
              <span>{userName === " " ? '\u00A0' : userName}</span>
            </Link>
            <Collapse isOpen={openAvatar}>
              <ul className="nav">
                <li>
                  <Link to="/admin/user-profile">
                    <span className="sidebar-mini-icon">MP</span>
                    <span className="sidebar-normal">My Profile</span>
                  </Link>
                </li>
              </ul>
            </Collapse>
          </div>
        </div>

        <Nav>
          {createLinks(props.routes)}
          <li
            className={
              location.pathname === "/auth/login" ? "active" : ""
            }
            key="logout"
          >
            <a
              href="#logout"
              className="nav-link"
              onClick={(e) => {
                e.preventDefault();
                handleLogout();
              }}
            >
              <i className="nc-icon nc-button-power" />
              <p>Logout</p>
            </a>
          </li>
        </Nav>
      </div>
    </div>
  );
}

export default Sidebar;
